import { useContext, useMemo } from 'react';
import { AuthContext } from 'src/lib/FirebaseContext';
import { resolveTenantCode } from 'src/lib/tenantRouting';
import type { TenantOrganisation } from 'src/features/admin/types';
import type { TenantMembershipRole } from 'src/types/auth';

export type AuthUser = {
  user_id?: number | null;
  external_id: string;
  email: string;
  firstname?: string | null;
  surname?: string | null;
  job_title?: string | null;
  organisation?: string | null;
  platform_admin?: boolean;
  onboarding_complete?: boolean;
  tenants: TenantOrganisation[];
  active_tenant_code?: string | null;
};

export type AuthUserWithHelpers = AuthUser & {
  getTenant: (tenantCode: string | null | undefined) => TenantOrganisation | undefined;
  getTenantRole: (tenantCode: string | null | undefined) => TenantMembershipRole | undefined;
  getActiveTenant: (pathname: string | undefined) => TenantOrganisation | undefined;
  getActiveTenantRole: (pathname: string | undefined) => TenantMembershipRole | undefined;
};

type AuthContextValue = {
  authState: string;
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser | null;
  method: string;
  login: (email: string, password: string) => Promise<void>;
  logout: (options?: { silent?: boolean }) => Promise<{ error: string } | void>;
  resetPassword: (email: string) => Promise<void>;
  validatePasswordResetCode: (oobCode: string) => Promise<{ email: string }>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  verifyEmail: (oobCode: string) => Promise<void>;
  updatePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  updateProfile: () => void;
  resendVerificationEmail: () => Promise<void>;
  completeMfa: () => void;
};

const useAuth = () => {
  // AuthContext lives in a .jsx file with no TS types; cast to the known shape.
  const auth = useContext(AuthContext) as AuthContextValue;

  return useMemo(() => {
    if (!auth.user) {
      return { ...auth, isPlatformAdmin: false, user: null } as typeof auth & {
        isPlatformAdmin: false;
        user: null;
      };
    }

    const user = auth.user as AuthUser;

    const getTenant = (tenantCode: string | null | undefined): TenantOrganisation | undefined =>
      user.tenants?.find((tenant) => tenant.code === tenantCode);

    const getTenantRole = (tenantCode: string | null | undefined): TenantMembershipRole | undefined =>
      getTenant(tenantCode)?.role as TenantMembershipRole | undefined;

    const getActiveTenant = (pathname: string | undefined): TenantOrganisation | undefined => {
      const activeTenantCode = resolveTenantCode(pathname, user.tenants);
      return getTenant(activeTenantCode);
    };

    const getActiveTenantRole = (pathname: string | undefined): TenantMembershipRole | undefined =>
      getActiveTenant(pathname)?.role as TenantMembershipRole | undefined;

    return {
      ...auth,
      isPlatformAdmin: user.platform_admin === true,
      user: {
        ...user,
        getTenant,
        getTenantRole,
        getActiveTenant,
        getActiveTenantRole
      } satisfies AuthUserWithHelpers
    };
  }, [auth]);
};

export default useAuth;
